/**
 * useLeadTimeTracking - Implied time tracking when user is on a lead-related page
 * Counts time only while the tab is visible and there has been recent activity
 * (pointer/keyboard/scroll). Pauses when the user switches tabs or is idle 30s+.
 */

import { useRef } from 'react';
import { useAuth } from '../utils/AuthContext';
import { saveLeadTimeEntry } from '../utils/leadTimeTrackingService';
import { useIdleAwareTimeSegments } from './useIdleAwareTimeSegments';

interface UseLeadTimeTrackingOptions {
  leadId: string | null;
  dealId?: string | null;
  proposalId?: string | null;
  enabled?: boolean;
}

export function useLeadTimeTracking({
  leadId,
  dealId,
  proposalId,
  enabled = true,
}: UseLeadTimeTrackingOptions) {
  const { user } = useAuth();
  const userRef = useRef(user);
  const dealIdRef = useRef(dealId);
  const proposalIdRef = useRef(proposalId);
  userRef.current = user;
  dealIdRef.current = dealId;
  proposalIdRef.current = proposalId;

  const { elapsedSeconds, flush } = useIdleAwareTimeSegments({
    enabled: enabled && Boolean(leadId) && Boolean(user?.id),
    sessionKey: leadId,
    trackElapsed: true,
    onSegment: async ({ start, end, minutes, sessionKey }) => {
      const currentUser = userRef.current;
      const currentLeadId = sessionKey || leadId;
      if (!currentLeadId || !currentUser?.id) return;
      await saveLeadTimeEntry({
        leadId: currentLeadId,
        userId: currentUser.id,
        userName: currentUser.name || 'Unknown',
        type: 'implied',
        minutes,
        startTime: start,
        endTime: end,
        dealId: dealIdRef.current ?? undefined,
        proposalId: proposalIdRef.current ?? undefined,
        createdAt: new Date(),
      });
    },
  });

  return { elapsedSeconds, flush };
}
